import type {
  ScheduleData,
  ContactsData,
  LoginResponse,
  ApiResponse,
  SelectedRecipient,
  Hymn,
} from '../types';

const API_BASE = '/api';

export async function fetchScheduleData(): Promise<ScheduleData> {
  const response = await fetch(`${API_BASE}/schedule`);
  if (!response.ok) {
    throw new Error('Failed to fetch schedule data');
  }
  return response.json();
}

export async function fetchHymns(): Promise<Hymn[]> {
  const response = await fetch(`${API_BASE}/hymns`);
  if (!response.ok) {
    throw new Error('Failed to fetch hymns');
  }
  return response.json();
}

export async function saveSchedule(data: ScheduleData): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/schedule`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return response.json();
}

export async function saveContacts(data: ContactsData): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/contacts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return response.json();
}

export async function login(password: string): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE}/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  return response.json();
}

export async function sendNotification(
  weekIndex: number,
  type: 'pianists' | 'special' | 'everyone'
): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/admin/notify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ weekIndex, type }),
  });
  return response.json();
}

export async function sendScheduleToSelected(
  weekIndex: number,
  selectedRecipients: SelectedRecipient[]
): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/admin/notify-selected`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ weekIndex, selectedRecipients }),
  });
  return response.json();
}

export async function sendCustomMessage(
  message: string,
  recipients: SelectedRecipient[]
): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/admin/custom-message`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, recipients }),
  });
  return response.json();
}

export async function resetDatabase(): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/reset`, {
    method: 'POST',
  });
  return response.json();
}

// === Choir ===

export interface ChoirContent {
  notes: string;
  info: string;
  schedule: string;
  fileLink: string;
  fileDownloadable: boolean;
}

export interface ChoirSubscriber {
  id: string;
  firstName: string;
  lastName: string;
  phone: string;
}

export async function fetchChoirContent(): Promise<ChoirContent> {
  const response = await fetch(`${API_BASE}/choir/content`);
  if (!response.ok) throw new Error('Failed to fetch choir content');
  return response.json();
}

export async function saveChoirContent(
  data: ChoirContent,
  password: string
): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/choir/content`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...data, password }),
  });
  return response.json();
}

export async function subscribeToChoir(
  firstName: string,
  lastName: string,
  phone: string
): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/choir/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ firstName, lastName, phone }),
  });
  return response.json();
}

export async function unsubscribeFromChoir(
  phone: string,
  lastName: string
): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/choir/unsubscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone, lastName }),
  });
  return response.json();
}

export async function fetchChoirSubscribers(): Promise<ChoirSubscriber[]> {
  const response = await fetch(`${API_BASE}/choir/subscribers`);
  if (!response.ok) throw new Error('Failed to fetch choir subscribers');
  return response.json();
}

export async function sendChoirMessage(
  message: string,
  subscriberIds: string[],
  password: string
): Promise<ApiResponse> {
  const response = await fetch(`${API_BASE}/choir/notify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, subscriberIds, password }),
  });
  return response.json();
}
